import { apiFetch } from "@/lib/apiClient";

// ── Types ─────────────────────────────────────────────────────────────────────

export interface PatientUser {
  id: string;
  name: string;
  email: string;
}

/**
 * Response shape for signin/refresh. The refresh token itself never appears here —
 * it arrives/rotates via an httpOnly Set-Cookie the browser handles automatically.
 * Verified against the live backend: no expiry field is actually sent today — the
 * real access-token lifetime (PATIENT_ACCESS_TOKEN_EXPIRE_MINUTES, currently 15) only
 * exists in the JWT itself. `expiresIn` is left optional so the auth store's fallback
 * kicks in instead of computing a NaN refresh delay.
 */
export interface AuthResponse {
  accessToken: string;
  expiresIn?: number;
  patient: PatientUser;
}

export interface BasicResponse {
  message?: string;
}

interface PatientApiResult<T> {
  data: T | null;
  error: string | null;
  code: string | null;
  retryAfterSeconds?: number;
  otpExpiresInSeconds?: number;
}

/** Normalizes the {data,error,code} triad into the {success,...} shape pages/OtpPanel expect. */
export function toAuthResult<T>(result: PatientApiResult<T>): { success: boolean; error?: string; code?: string; retryAfterSeconds?: number } {
  if (result.data) return { success: true };
  return { success: false, error: result.error ?? undefined, code: result.code ?? undefined, retryAfterSeconds: result.retryAfterSeconds };
}

// ── Core fetch wrapper ────────────────────────────────────────────────────────
// The backend's global exception handler reshapes every error — across both
// portals — into { code, message }, so there's nothing portal-specific to map here.

async function request<T>(
  path: string,
  options: { method: string; body?: object; protected?: boolean },
): Promise<PatientApiResult<T>> {
  const { data, error } = await apiFetch<T>(path, {
    method: options.method,
    body: options.body ? JSON.stringify(options.body) : undefined,
    portal: "patient",
    skipAuth: !options.protected,
  });

  if (error) {
    return {
      data: null,
      error: error.message,
      code: error.code,
      retryAfterSeconds: error.retryAfterSeconds,
      otpExpiresInSeconds: error.otpExpiresInSeconds,
    };
  }
  return { data, error: null, code: null };
}

const post = <T>(path: string, body: object) => request<T>(path, { method: "POST", body });

// ── Session endpoints ─────────────────────────────────────────────────────────
// Paths and refresh-cookie scope (`medsynciq_patient_refresh`, path `/api/patient/auth`)
// are fixed by the backend contract — signin/refresh/signout must stay under that prefix.

/** On an unverified account the backend responds with code EMAIL_NOT_VERIFIED (HTTP 403). */
export const signInApi = (email: string, password: string) =>
  post<AuthResponse>("/api/patient/auth/signin", { email, password });

/** Reads the httpOnly refresh cookie server-side and rotates it; no token is passed from JS. */
export const refreshApi = () =>
  request<AuthResponse>("/api/patient/auth/refresh", { method: "POST" });

/** Revokes only the current session's refresh token. */
export const signOutApi = () =>
  request<BasicResponse>("/api/patient/auth/signout", { method: "POST", protected: true });

// ── Signup / OTP / password-reset endpoints ──────────────────────────────────

/** Creates an unverified account — backend emails a 6-digit OTP to confirm the address. */
export const signUpApi = (
  firstName: string,
  lastName: string,
  email: string,
  password: string,
  confirmPassword: string,
) => post<BasicResponse>("/api/patient/auth/signup", {
  firstName,
  lastName,
  email,
  password,
  confirmPassword,
});

/** wasClaim is true only when this signup just finalized a Dedicated Profile
 * claim (see PatientService.verify_otp) — tells the frontend to show the
 * one-time "here's what to do next" guidance card instead of the plain
 * success notice. creatorName personalizes that card's copy. */
export interface VerifySignupOtpResponse extends BasicResponse {
  wasClaim?: boolean;
  creatorName?: string | null;
}

/** Confirms the signup OTP and marks the account as verified. */
export const verifySignupOtpApi = (email: string, otp: string) =>
  post<VerifySignupOtpResponse>("/api/patient/auth/verify-otp", { email, otp });

/** Re-sends the signup verification OTP. */
export const resendSignupOtpApi = (email: string) =>
  post<BasicResponse>("/api/patient/auth/resend-otp", { email });

/** Sends a password-reset OTP to the given email; also used to resend it. */
export const forgotPasswordApi = (email: string) =>
  post<BasicResponse>("/api/patient/auth/forgot-password", { email });

/** Response shape for verify-reset-otp — resetToken is required by resetPasswordApi below. */
export interface VerifyResetOtpResponse extends BasicResponse {
  resetToken: string;
}

/** Confirms the password-reset OTP; on success, the backend issues a one-time resetToken. */
export const verifyResetOtpApi = (email: string, otp: string) =>
  post<VerifyResetOtpResponse>("/api/patient/auth/verify-reset-otp", { email, otp });

/** Sets a new password using the resetToken issued by verifyResetOtpApi (not the OTP itself). */
export const resetPasswordApi = (email: string, resetToken: string, newPassword: string, confirmPassword: string) =>
  post<BasicResponse>("/api/patient/auth/reset-password", { email, resetToken, newPassword, confirmPassword });
